// PE Delivery Loop — Supabase-direct phase-progress persistence.
//
// The locked-sequence engine is pure (no I/O). This module is the persistence
// adapter both surfaces share: it reads pe_chapter_phases (for the chapter's
// phase ids) and pe_assignment_phase_progress (the student's completions), and
// writes a completion row after each engine completeStep.
//
// RLS already supports this directly from the authenticated student's client:
//   - pe_chapter_phases:               read = any authenticated
//   - pe_assignment_phase_progress:    ALL = student's own rows
// So no backend endpoint is involved (pe.service.ts is untouched).

import supabase from "../utils/supabase";
import type { StepId } from "../engine/sequenceEngine";
import { PHASE_TO_STEP, STEP_TO_PHASE, isKnownPhase } from "../engine/stepPhaseMap";

/** A phase definition row for a chapter (subset we use). */
export interface ChapterPhase {
  id: number;
  phase: string;
  phase_order: number;
}

/** Fetch the chapter's phase rows (ascending phase_order). */
export async function fetchChapterPhases(chapterId: string): Promise<ChapterPhase[]> {
  const { data, error } = await supabase
    .from("pe_chapter_phases")
    .select("id, phase, phase_order")
    .eq("chapter_id", Number(chapterId))
    .order("phase_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChapterPhase[];
}

/** Fetch the student's completed-phase rows for this assignment. */
export async function fetchProgressPhaseIds(
  assignmentId: string,
  studentId: string,
): Promise<Set<number>> {
  const { data, error } = await supabase
    .from("pe_assignment_phase_progress")
    .select("chapter_phase_id, completed_at")
    .eq("assignment_id", assignmentId)
    .eq("student_id", studentId);
  if (error) throw error;
  const ids = new Set<number>();
  for (const row of (data ?? []) as { chapter_phase_id: number; completed_at: string | null }[]) {
    if (row.completed_at) ids.add(row.chapter_phase_id);
  }
  return ids;
}

/** Map a StepId to its chapter_phase_id, given the chapter's phase rows. */
export function chapterPhaseIdForStep(
  step: StepId,
  phases: ChapterPhase[],
): number | null {
  const phaseName = STEP_TO_PHASE[step];
  if (!phaseName) return null; // e.g. summative (deferred enum)
  const row = phases.find((p) => p.phase === phaseName);
  return row ? row.id : null;
}

/**
 * Resolve which steps are already complete, FAIL-SOFT (Phase-2 guard 2).
 *
 * We sort the chapter's phase rows by phase_order and walk them: a phase is a
 * "completed step" only if (a) its enum is known, (b) it has a progress row,
 * and (c) it appears in canonical order. A row that is out of order, has an
 * unknown phase, or doesn't map to a step is LOGGED and skipped — it never
 * throws and never blocks the student screen. Returns the completed StepIds in
 * canonical order.
 */
export function hydrateCompletedSteps(
  phases: ChapterPhase[],
  completedPhaseIds: Set<number>,
): StepId[] {
  const ordered = [...phases].sort((a, b) => a.phase_order - b.phase_order);
  const completed: StepId[] = [];
  let lastOrder = -Infinity;

  for (const p of ordered) {
    if (p.phase_order < lastOrder) {
      console.warn(
        `[peProgress] phase row out of order (id=${p.id}, order=${p.phase_order}) — skipping, degrading gracefully`,
      );
      continue;
    }
    lastOrder = p.phase_order;

    if (!completedPhaseIds.has(p.id)) continue;
    if (!isKnownPhase(p.phase)) {
      console.warn(`[peProgress] unknown phase "${p.phase}" (id=${p.id}) — skipping`);
      continue;
    }
    completed.push(PHASE_TO_STEP[p.phase]);
  }
  return completed;
}

/**
 * Persist a step completion. Idempotent via the composite unique
 * (assignment_id, student_id, chapter_phase_id). Returns false (and logs) if
 * the step has no DB phase (e.g. summative) rather than throwing.
 */
export async function persistStepComplete(args: {
  assignmentId: string;
  studentId: string;
  step: StepId;
  phases: ChapterPhase[];
}): Promise<boolean> {
  const chapterPhaseId = chapterPhaseIdForStep(args.step, args.phases);
  if (chapterPhaseId == null) {
    console.warn(`[peProgress] no chapter_phase for step "${args.step}" — not persisted`);
    return false;
  }
  const { error } = await supabase
    .from("pe_assignment_phase_progress")
    .upsert(
      {
        assignment_id: args.assignmentId,
        student_id: args.studentId,
        chapter_phase_id: chapterPhaseId,
        completed_at: new Date().toISOString(),
      },
      { onConflict: "assignment_id,student_id,chapter_phase_id" },
    );
  if (error) throw error;
  return true;
}
